import React from 'react';
import { useNavigate } from 'react-router-dom';
import type { LocaleCode, OperationActivityDetail } from '@/api/models';
import {
    useOperationActivityDetailQuery,
    useReverseJournalEntryMutation,
} from '@/api/queries';
import { PATH } from '@/router/PATH';
import { toReverseError, type ReverseError } from '../../activityMeta';

type UseActivityDetailDrawerOptions = {
    /** The record to show; `null` closes the drawer and fetches nothing. */
    recordId: string | null;
    locale: LocaleCode;
};

export type UseActivityDetailDrawerResult = {
    detail: OperationActivityDetail | null;
    isLoading: boolean;
    /** A failed read. Rendered inside the drawer, never thrown. */
    hasError: boolean;
    isReversing: boolean;
    /** The last refusal from the reverse call, anchored. */
    reverseError: ReverseError | null;
    onReverse: (reason: string) => void;
    onDismissReverseError: () => void;
    onOpenEntry: (entryId: string, date: string) => void;
};

/**
 * Drives the recorded-operation detail drawer.
 *
 * Self-served, like the payroll-run and fixed-asset drawers: it owns the read
 * for the record it was handed rather than being passed a row the list already
 * had. That is what lets it show the **entry's own lines**, which the list does
 * not carry — and what makes it correct after a reversal, since the mutation
 * invalidates the resource and this refetches.
 *
 * **A failed read is rendered here, not thrown.** Every other page in this
 * folder throws to its boundary, and a drawer is the one place that would be
 * wrong: throwing unmounts the register behind it and replaces a working screen
 * with a full-page error because one row could not be opened. The drawer says so
 * and stays closeable.
 *
 * **Reversal is the journal's.** `useReverseJournalEntryMutation` against the
 * entry this operation posted — no operations-side write exists, and none should:
 * a second reversal path would be a second set of rules about the same act.
 */
export const useActivityDetailDrawer = ({
    recordId,
    locale,
}: UseActivityDetailDrawerOptions): UseActivityDetailDrawerResult => {
    const navigate = useNavigate();
    const [reverseError, setReverseError] = React.useState<ReverseError | null>(
        null,
    );

    const query = useOperationActivityDetailQuery(recordId, locale, true);
    const detail = query.data?.success ? query.data.response : null;
    const hasError = query.data ? !query.data.success : Boolean(query.error);

    const reverse = useReverseJournalEntryMutation();

    // A newly opened record starts with a clean slate: a refusal about the
    // previous row has nothing to say about this one.
    React.useEffect(() => {
        setReverseError(null);
    }, [recordId]);

    const onReverse = React.useCallback(
        (reason: string) => {
            if (!detail) {
                return;
            }
            setReverseError(null);
            reverse.mutate(
                {
                    id: detail.row.entryId,
                    // No `force`. That flag exists for an entry whose operational
                    // *document* keeps standing while its books are undone; a
                    // recorded operation has no such document, and the server
                    // asks only for a reason (which it requires — the client
                    // gates on it first so the round trip is rarely spent).
                    options: { reason },
                },
                {
                    onSuccess: (result) => {
                        if (!result.success) {
                            setReverseError(toReverseError(result.error));
                        }
                    },
                },
            );
        },
        [detail, reverse],
    );

    const onOpenEntry = React.useCallback(
        (entryId: string, date: string) => {
            // The Journal **tab**, scoped to the entry's own date on both sides.
            // That tab takes its window from the Books shell and cannot widen
            // it, and a recorded operation is routinely dated outside the
            // shell's default month.
            navigate(
                PATH.ACCOUNTING.BOOKS.BIND_JOURNAL_FOR_ENTRY(entryId, {
                    from: date,
                    to: date,
                }),
            );
        },
        [navigate],
    );

    return {
        detail,
        isLoading: recordId !== null && query.isLoading,
        hasError,
        isReversing: reverse.isPending,
        reverseError,
        onReverse,
        onDismissReverseError: React.useCallback(
            () => setReverseError(null),
            [],
        ),
        onOpenEntry,
    };
};
